import { type AssessmentSelectDto, type TechnologySelectDto } from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { Button } from '@components/ui/button';
import { useAppForm } from '@components/ui/form/form';
import { errorToast } from '@hooks/toast.hook';
import { useCreateCyberAssessment } from '@queries/cyber-assessment.query';
import {
  type CyberAssessmentCreateFormData,
  cyberAssessmentCreateSourceAssessmentIdSchema,
  cyberAssessmentCreateTechnologyTypesSchema,
} from '@schemas/cyber-assessment.schema';
import { getDefaultCompletedMilitaryTrlSourceAssessmentId } from '@utils/cyber-assessment/model.util';
import { createFormInvalidHandler } from '@utils/form.util';
import { Loader2Icon } from 'lucide-react';
import React, { useEffect, useMemo } from 'react';
import { TechnologyCreateCyberAssessmentTypesField } from './technology-create-cyber-assessment-types-field.component';

type TechnologyCreateCyberAssessmentModalProps = {
  isOpen: boolean;
  militaryTrlAssessments: AssessmentSelectDto[];
  onClose: () => void;
  onCreated: (cyberAssessmentId: string) => void;
  technology: TechnologySelectDto;
};

export const TechnologyCreateCyberAssessmentModal: React.FC<TechnologyCreateCyberAssessmentModalProps> = (props) => {
  const { isOpen, militaryTrlAssessments, onClose, onCreated, technology } = props;
  const { mutateAsync: createCyberAssessment, isPending } = useCreateCyberAssessment(technology.id);
  const defaultSourceAssessmentId = getDefaultCompletedMilitaryTrlSourceAssessmentId(militaryTrlAssessments) ?? '';
  const defaultTechnologyTypes = useMemo(() => [technology.type], [technology.type]);

  const sourceOptions = useMemo(
    () =>
      militaryTrlAssessments.map((assessment) => ({
        label: `TRL ${assessment.score} • ${assessment.use.name} • ${assessment.domain.name}`,
        value: assessment.id,
      })),
    [militaryTrlAssessments],
  );

  const handleSubmitInvalid = createFormInvalidHandler<CyberAssessmentCreateFormData>();
  const form = useAppForm({
    defaultValues: {
      sourceAssessmentId: defaultSourceAssessmentId,
      technologyTypes: defaultTechnologyTypes,
    } satisfies CyberAssessmentCreateFormData,
    onSubmit: async ({ value }) => {
      await createCyberAssessment(value, {
        onError: (error) => {
          errorToast({ description: error.message || 'Failed to create Cyber Assessment.' });
        },
        onSuccess: (data) => {
          handleClose();
          onCreated(data.id);
        },
      });
    },
    onSubmitInvalid: (props) => {
      form.setFieldMeta('sourceAssessmentId', (meta) => ({ ...meta, isTouched: true }));
      form.setFieldMeta('technologyTypes', (meta) => ({ ...meta, isTouched: true }));
      handleSubmitInvalid(props);
    },
  });

  useEffect(() => {
    if (!isOpen || militaryTrlAssessments.length === 0) {
      return;
    }

    form.setFieldValue('sourceAssessmentId', defaultSourceAssessmentId);
    form.setFieldValue('technologyTypes', defaultTechnologyTypes);
  }, [defaultSourceAssessmentId, defaultTechnologyTypes, form, isOpen, militaryTrlAssessments.length]);

  const handleClose = () => {
    form.reset();
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  const handleSubmit = async (): Promise<void> => {
    await form.validateAllFields('submit');
    await form.handleSubmit();
  };

  return (
    <Modal
      centered
      isOpen={isOpen}
      onClose={handleClose}
      header={{
        className: 'text-left',
        title: 'Create Cyber Assessment',
      }}
      footer={{
        rightButton: (
          <div className="flex gap-2 justify-end">
            <Button
              disabled={isPending}
              onClick={handleClose}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              disabled={isPending || militaryTrlAssessments.length === 0}
              onClick={() => void handleSubmit()}
              type="button"
            >
              {isPending && <Loader2Icon className="size-3 mr-1 animate-spin" />}
              Create
            </Button>
          </div>
        ),
      }}
      width="640px"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void handleSubmit();
        }}
      >
        <form.AppForm>
          <div className="flex flex-col gap-4">
            <form.AppField
              name="sourceAssessmentId"
              validators={{
                onSubmit: cyberAssessmentCreateSourceAssessmentIdSchema,
              }}
            >
              {(field) => (
                <field.SelectPopoverField
                  allowClear={false}
                  disabled={militaryTrlAssessments.length === 0}
                  label="Military TRL Source"
                  options={sourceOptions}
                  placeholder={
                    militaryTrlAssessments.length > 0
                      ? 'Select a completed Military TRL assessment'
                      : 'No completed Military TRL assessments available'
                  }
                  required
                />
              )}
            </form.AppField>

            <form.AppField
              mode="array"
              name="technologyTypes"
              validators={{
                onSubmit: cyberAssessmentCreateTechnologyTypesSchema,
              }}
            >
              {(field) => {
                const selectedTechnologyTypes = field.state.value ?? [];

                return (
                  <TechnologyCreateCyberAssessmentTypesField
                    onCheckedChange={({ checked, technologyType }) => {
                      if (checked) {
                        if (!selectedTechnologyTypes.includes(technologyType)) {
                          field.pushValue(technologyType);
                        }

                        return;
                      }

                      const optionIndex = selectedTechnologyTypes.indexOf(technologyType);

                      if (optionIndex >= 0) {
                        field.removeValue(optionIndex);
                      }
                    }}
                    selectedTechnologyTypes={selectedTechnologyTypes}
                  />
                );
              }}
            </form.AppField>
          </div>
        </form.AppForm>
      </form>
    </Modal>
  );
};
